import React from 'react'
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  saveIntegration,
  testIntegration,
  setTelegramWebhook,
  getTelegramInfo,
  type IntegrationProvider,
  type TelegramWebhookResponse,
  type TelegramInfoResponse,
} from '../../api/integrations'
import QRCodeBox from '../common/QRCodeBox'

type TelegramFormProps = {
  stationId: number
  provider?: IntegrationProvider
  initializing?: boolean
  onSaved: () => void | Promise<void>
  onNotify: (severity: 'success' | 'error', message: string) => void
}

type FormErrors = {
  botToken?: string
}

type ModalVariant = 'success' | 'error' | 'info'

type ModalState = {
  open: boolean
  variant: ModalVariant
  message?: string
}

const TELEGRAM_TOKEN_REGEX = /^[0-9]+:[A-Za-z0-9_-]+$/

const getErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error) {
    return err.message
  }

  if (err && typeof err === 'object') {
    if ('message' in err) {
      return String((err as { message?: unknown }).message ?? fallback)
    }

    if ('code' in err) {
      return String((err as { code?: unknown }).code ?? fallback)
    }
  }

  return fallback
}

const TelegramForm: React.FC<TelegramFormProps> = ({
  stationId,
  provider,
  initializing = false,
  onSaved,
  onNotify,
}) => {
  const [botToken, setBotToken] = React.useState('')
  const [errors, setErrors] = React.useState<FormErrors>({})
  const [saving, setSaving] = React.useState(false)
  const [testing, setTesting] = React.useState(false)
  const [modalTesting, setModalTesting] = React.useState(false)
  const [settingWebhook, setSettingWebhook] = React.useState(false)
  const [modalState, setModalState] = React.useState<ModalState | null>(null)
  const [info, setInfo] = React.useState<TelegramInfoResponse | null>(null)
  const [infoLoading, setInfoLoading] = React.useState(false)

  const status = provider?.status ?? 'not_configured'
  const providerError = typeof provider?.error === 'string' && provider.error !== '' ? provider.error : null
  const safeSettings: Record<string, unknown> = status === 'corrupt' ? {} : provider?.settings ?? {}
  const storedBotToken = typeof safeSettings.bot_token === 'string' ? safeSettings.bot_token : ''
  const hasStoredToken = status === 'ok' && storedBotToken === '__REDACTED__'
  const tokenHashFromSettings = typeof safeSettings.token_hash === 'string' ? safeSettings.token_hash : ''
  const webhookFromSettings = typeof safeSettings.webhook_url === 'string' ? safeSettings.webhook_url : ''
  const usernameFromSettings = typeof safeSettings.bot_username === 'string' ? safeSettings.bot_username : ''

  const tokenHash = info?.token_hash ?? tokenHashFromSettings
  const webhookUrl = info?.webhook_url ?? webhookFromSettings
  const botUsername = info?.username ?? usernameFromSettings
  const usernameError = info?.username_error ?? null
  const configured = status === 'ok' && tokenHash !== ''

  React.useEffect(() => {
    setBotToken('')
    setErrors({})
    setInfo(null)
  }, [provider?.id, provider?.settings])

  const copyToClipboard = React.useCallback(
    async (value: string, successMessage: string) => {
      if (!value) {
        return
      }
      try {
        await navigator.clipboard.writeText(value)
        onNotify('success', successMessage)
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to copy to clipboard.')
        onNotify('error', message)
      }
    },
    [onNotify],
  )

  const fetchInfo = React.useCallback(
    async (suppressNotify = false) => {
      if (!stationId) {
        return null
      }

      setInfoLoading(true)

      try {
        const data = await getTelegramInfo(stationId)
        setInfo(data)
        return data
      } catch (error) {
        const message = getErrorMessage(error, 'Unable to load Telegram bot details.')
        if (!suppressNotify) {
          onNotify('error', message)
        }
        setInfo({ ok: false, error: message })
        return null
      } finally {
        setInfoLoading(false)
      }
    },
    [onNotify, stationId],
  )

  const runTestConnection = React.useCallback(async () => {
    try {
      const result = await testIntegration('telegram', stationId)

      if (result.ok) {
        const message = result.details ?? 'Telegram connection successful.'
        onNotify('success', message)
        await fetchInfo(true)
        return true
      }

      const message = result.error ?? 'Telegram test failed.'
      onNotify('error', message)
      return false
    } catch (error) {
      const message = getErrorMessage(error, 'Telegram test failed.')
      onNotify('error', message)
      return false
    }
  }, [stationId, onNotify, fetchInfo])

  const runSetWebhook = React.useCallback(async () => {
    setSettingWebhook(true)

    try {
      const response = await setTelegramWebhook(stationId)
      setInfo(response)
      await Promise.resolve(onSaved())
      await fetchInfo(true)
      return { ok: true as const, response }
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to set Telegram webhook.')
      return { ok: false as const, error: message }
    } finally {
      setSettingWebhook(false)
    }
  }, [stationId, onSaved, fetchInfo])

  const validateToken = (): string | null => {
    const trimmed = botToken.trim()
    const nextErrors: FormErrors = {}

    if (trimmed === '') {
      if (hasStoredToken) {
        return '__UNCHANGED__'
      }
      nextErrors.botToken = 'Bot token is required.'
    } else if (!TELEGRAM_TOKEN_REGEX.test(trimmed)) {
      nextErrors.botToken = 'Enter a valid Telegram bot token.'
    } else {
      return trimmed
    }

    setErrors(nextErrors)
    return null
  }

  const handleSave = async () => {
    if (saving || settingWebhook) {
      return
    }

    const payloadToken = validateToken()

    if (payloadToken === null) {
      return
    }

    setSaving(true)

    try {
      const response = await saveIntegration('telegram', stationId, {
        bot_token: payloadToken,
      })

      if (!response.ok) {
        throw new Error('Save request failed.')
      }

      await Promise.resolve(onSaved())
      setBotToken('')

      const webhookResult = await runSetWebhook()

      if (webhookResult.ok) {
        setModalState({ open: true, variant: 'success' })
        onNotify('success', 'Telegram bot connected.')
      } else {
        const message = webhookResult.error ?? 'Unable to set Telegram webhook.'
        setModalState({ open: true, variant: 'error', message })
        onNotify('error', message)
      }
    } catch (error) {
      const message = getErrorMessage(error, 'Unable to save Telegram settings.')
      onNotify('error', message)
    } finally {
      setSaving(false)
    }
  }

  const handleTestConnection = async () => {
    if (testing) {
      return
    }

    setTesting(true)
    try {
      await runTestConnection()
    } finally {
      setTesting(false)
    }
  }

  const handleModalTestConnection = async () => {
    if (modalTesting) {
      return
    }

    setModalTesting(true)
    try {
      await runTestConnection()
    } finally {
      setModalTesting(false)
    }
  }

  const handleOpenModal = async () => {
    await fetchInfo(true)
    setModalState({ open: true, variant: 'info' })
  }

  const handleCloseModal = () => {
    setModalState(null)
  }

  const handleResetWebhook = async () => {
    if (settingWebhook) {
      return
    }

    const result = await runSetWebhook()

    if (result.ok) {
      setModalState({ open: true, variant: 'success' })
      onNotify('success', 'Telegram webhook configured.')
    } else {
      const message = result.error ?? 'Unable to set Telegram webhook.'
      setModalState({ open: true, variant: 'error', message })
      onNotify('error', message)
    }
  }

  const isDirty = botToken.trim() !== ''

  if (initializing && !provider) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
        </Stack>
      </Paper>
    )
  }

  const statusChip = (() => {
    switch (status) {
      case 'ok':
        return <Chip label="Connected" color="success" size="small" />
      case 'corrupt':
        return <Chip label="Error" color="error" variant="outlined" size="small" />
      default:
        return <Chip label="Not configured" variant="outlined" size="small" />
    }
  })()

  const shareLink = botUsername ? `https://t.me/${botUsername}` : ''
  const deepLinks = botUsername
    ? ['breakfastshow', 'drive-time'].map((code) => `https://t.me/${botUsername}?start=${encodeURIComponent(code)}`)
    : []

  const modalIntro = () => {
    if (modalState?.variant === 'error') {
      return (
        <Alert severity="error">{modalState.message ?? 'Telegram setWebhook error. Please try again.'}</Alert>
      )
    }

    if (configured) {
      return (
        <Alert severity="success" variant="outlined">
          Your bot is connected. Listeners DM the bot — not a phone number.
        </Alert>
      )
    }

    return (
      <Alert severity="info" variant="outlined">
        Configure your bot token and webhook to start receiving Telegram messages.
      </Alert>
    )
  }

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Typography variant="h6">Telegram</Typography>
              <Typography variant="body2" color="text.secondary">
                Connect your Telegram bot to ingest audience messages via secure webhook.
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              {statusChip}
              <Tooltip title="How this works">
                <span>
                  <IconButton onClick={handleOpenModal} size="small">
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Stack>

          <Stack spacing={2}>
            {providerError && status === 'corrupt' ? (
              <Alert severity="error">Configuration could not be decrypted: {providerError}</Alert>
            ) : null}

            <TextField
              label="Bot Token"
              type="password"
              value={botToken}
              onChange={(event) => {
                setBotToken(event.target.value)
                if (errors.botToken) {
                  setErrors((prev) => ({ ...prev, botToken: undefined }))
                }
              }}
              placeholder={hasStoredToken ? 'Leave blank to keep the existing token' : undefined}
              helperText={errors.botToken}
              error={Boolean(errors.botToken)}
              fullWidth
              autoComplete="off"
            />

            <TextField
              label="Webhook URL"
              value={webhookUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => copyToClipboard(webhookUrl, 'Webhook URL copied to clipboard.')}
                          disabled={!webhookUrl}
                          aria-label="Copy webhook URL"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </InputAdornment>
                ),
              }}
            />

            {tokenHash ? <Alert severity="info">Webhook token hash: {tokenHash}</Alert> : null}

            {botUsername ? (
              <Alert severity="success" variant="outlined">
                Bot username detected: <strong>@{botUsername}</strong>
              </Alert>
            ) : null}

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleTestConnection} disabled={testing || !configured}>
                {testing ? <CircularProgress size={16} /> : 'Test Connection'}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || settingWebhook || !isDirty}
              >
                {saving || settingWebhook ? <CircularProgress size={16} /> : 'Save'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Dialog open={modalState?.open ?? false} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle>Telegram onboarding</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {modalIntro()}
            {usernameError ? <Alert severity="warning">{usernameError}</Alert> : null}
            {info?.error ? <Alert severity="error">{info.error}</Alert> : null}
            {infoLoading ? (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 2 }}>
                <CircularProgress />
              </Stack>
            ) : (
              <Stack spacing={2}>
                {botUsername ? (
                  <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }}>
                    <Stack spacing={1} sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle1">Share your bot</Typography>
                      <TextField
                        label="Bot link"
                        value={shareLink}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Copy">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => copyToClipboard(shareLink, 'Bot link copied to clipboard.')}
                                    disabled={!shareLink}
                                    aria-label="Copy bot link"
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                      <Stack spacing={1}>
                        <Typography variant="subtitle2">Deep-link examples</Typography>
                        {deepLinks.map((link) => (
                          <TextField
                            key={link}
                            value={link}
                            InputProps={{
                              readOnly: true,
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Copy">
                                    <span>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(link, 'Deep link copied to clipboard.')}
                                        aria-label="Copy deep link"
                                      >
                                        <ContentCopyIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                            fullWidth
                          />
                        ))}
                      </Stack>
                    </Stack>
                    <QRCodeBox value={shareLink} />
                  </Stack>
                ) : (
                  <Alert severity="info">
                    We couldn’t fetch the username yet — use Test Connection or DM the bot to verify.
                  </Alert>
                )}

                <Stack spacing={1}>
                  <Typography variant="subtitle1">Group usage</Typography>
                  <Typography color="text.secondary">
                    Add the bot to a group. To receive all messages, disable privacy in @BotFather (<code>/setprivacy</code> →
                    Disable).
                  </Typography>
                </Stack>

                <Stack spacing={1}>
                  <Typography variant="subtitle1">Troubleshooting</Typography>
                  <Stack component="ul" sx={{ pl: 3 }} spacing={0.5}>
                    <Typography component="li" color="text.secondary">
                      Ensure your site uses HTTPS and is publicly reachable.
                    </Typography>
                    <Typography component="li" color="text.secondary">
                      Rotate the bot token in @BotFather if you suspect it was compromised.
                    </Typography>
                    <Typography component="li" color="text.secondary">
                      Allow outbound HTTPS to api.telegram.org (firewall/WAF whitelisting).
                    </Typography>
                    <Typography component="li" color="text.secondary">
                      Use <code>getWebhookInfo</code> in the Bot API to inspect the current webhook status.
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleModalTestConnection} disabled={modalTesting || !configured}>
            {modalTesting ? <CircularProgress size={16} /> : 'Test Connection'}
          </Button>
          <Button onClick={handleResetWebhook} disabled={settingWebhook || !configured}>
            {settingWebhook ? <CircularProgress size={16} /> : 'Re-set Webhook'}
          </Button>
          <Button onClick={handleCloseModal} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default TelegramForm
